
import { createClient } from '@supabase/supabase-js';
import { isSSR } from '../isSSR';

// Supabase URL e chiave di servizio - lette dalle variabili d'ambiente
const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL || process.env.SUPABASE_URL || 'https://qldjmxfimvxskdbastkx.supabase.co';
const supabaseServiceKey = import.meta.env?.VITE_SUPABASE_SERVICE_ROLE_KEY || process.env.SUPABASE_SERVICE_KEY;
const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY || process.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InFsZGpteGZpbXZ4c2tkYmFzdGt4Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3Mzk3Mjk5ODUsImV4cCI6MjA1NTMwNTk4NX0.yXHezaqqVqD6tK9IhkJ-Lw6FaE1mhjt-hNCsu7Tvf-w';

// Log per debugging
console.log('[Supabase Direct] Service key available:', !!supabaseServiceKey);

// Meccanismo di storage personalizzato per compatibilità SSR
const customStorage = {
  getItem: (key: string) => {
    try {
      if (isSSR()) {
        console.log('[Supabase Direct] Skipping localStorage.getItem in SSR mode');
        return null;
      }
      return localStorage.getItem(key);
    } catch (e) {
      console.warn('[Supabase Direct] Storage getItem error:', e);
      return null;
    }
  },
  setItem: (key: string, value: string) => {
    try {
      if (isSSR()) {
        console.log('[Supabase Direct] Skipping localStorage.setItem in SSR mode');
        return;
      }
      localStorage.setItem(key, value);
    } catch (e) {
      console.warn('[Supabase Direct] Storage setItem error:', e);
    }
  },
  removeItem: (key: string) => {
    try {
      if (isSSR()) {
        console.log('[Supabase Direct] Skipping localStorage.removeItem in SSR mode');
        return;
      }
      localStorage.removeItem(key);
    } catch (e) {
      console.warn('[Supabase Direct] Storage removeItem error:', e);
    }
  },
};

// Crea un client Supabase con la chiave di servizio se disponibile, altrimenti usa la chiave anonima
// Questo client ha privilegi elevati e dovrebbe essere usato solo per operazioni amministrative
export const supabaseDirectClient = createClient(
  supabaseUrl,
  supabaseServiceKey || supabaseAnonKey,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      storageKey: 'gildy_direct_token',
      storage: customStorage,
    },
    global: {
      headers: {
        'x-gildy-client': 'admin-service',
        'Content-Type': 'application/json'
      },
    },
    db: {
      schema: 'public'
    }
  }
);

// Funzione per verificare se una tabella esiste
export const checkTableExists = async (tableName: string): Promise<boolean> => {
  if (!tableName) return false;
  
  try {
    console.log(`[Supabase Direct] Checking if table ${tableName} exists...`);
    const { error } = await supabaseDirectClient
      .from(tableName)
      .select('count')
      .limit(0);
    
    if (!error) {
      console.log(`[Supabase Direct] Table ${tableName} exists`);
      return true;
    }
    
    if (error.code === '42P01' || error.message.includes('does not exist')) {
      console.log(`[Supabase Direct] Table ${tableName} doesn't exist`);
      return false;
    }
    
    console.error(`[Supabase Direct] Error checking table ${tableName}:`, error);
    return false;
  } catch (err) {
    console.error(`[Supabase Direct] Exception checking table ${tableName}:`, err);
    return false;
  }
};

// Funzioni di utilità
export const checkDirectClient = () => {
  console.log('[Supabase Direct] Client status check');
  
  // Verifica se la chiave di servizio è disponibile
  if (supabaseServiceKey) {
    console.log('[Supabase Direct] Using service role key');
  } else {
    console.warn('[Supabase Direct] Service role key not found, using anon key - some operations may fail');
  }
  
  return true;
};
